import { COUNT_Y_AXIS, linearSeries } from '@components/Common/Charts/chartAxes';
import EmptyState from '@components/Common/EmptyState';
import ErrorMessage from '@components/Common/ErrorMessage';
import MetricTable, { type MetricColumn } from '@components/Common/MetricTable';
import { Panel } from '@components/Common/Panel';
import SkeletonLoader from '@components/Common/Skeleton/SkeletonLoader';
import StatTile from '@components/Common/StatTile';
import { formatDayMonth } from '@helpers/date';
import BlockOutlinedIcon from '@mui/icons-material/BlockOutlined';
import HighlightOffOutlinedIcon from '@mui/icons-material/HighlightOffOutlined';
import ReportGmailerrorredOutlinedIcon from '@mui/icons-material/ReportGmailerrorredOutlined';
import UnsubscribeOutlinedIcon from '@mui/icons-material/UnsubscribeOutlined';
import { Box, Typography } from '@mui/material';
import { LineChart } from '@mui/x-charts/LineChart';
import axios from 'axios';
import { useEffect, useState } from 'react';

export interface EmailBounceSummary {
  total: number;
  permanent: number;
  transient: number;
  complaints: number;
  rejects: number;
  suppressedTotal: number;
  byDay: { date: string; bounces: number; complaints: number }[];
  bySender: { from: string; count: number }[];
  topReasons: { reason: string; count: number }[];
}

type SenderRow = EmailBounceSummary['bySender'][number];
type ReasonRow = EmailBounceSummary['topReasons'][number];

const SENDER_COLUMNS: MetricColumn<SenderRow>[] = [
  { key: 'from', header: 'Sender', variant: 'name', render: (r) => r.from, sortValue: (r) => r.from },
  { key: 'count', header: 'Problems', render: (r) => r.count.toLocaleString('en-IN'), sortValue: (r) => r.count },
];

const REASON_COLUMNS: MetricColumn<ReasonRow>[] = [
  { key: 'reason', header: 'Reason', variant: 'name', render: (r) => r.reason, sortValue: (r) => r.reason },
  { key: 'count', header: 'Times', render: (r) => r.count.toLocaleString('en-IN'), sortValue: (r) => r.count },
];

const n = (v: number | undefined) => (v ?? 0).toLocaleString('en-IN');

/**
 * Bounces and complaints, from the SES events the backend records.
 *
 * Its own request rather than a field on the analytics payload: the numbers come from a different
 * collection (email events, not send records), and a failure here should cost this panel only,
 * never the rest of the page.
 */
const BouncePanel: React.FC<{ days: number; refreshKey?: number }> = ({ days, refreshKey = 0 }) => {
  const [data, setData] = useState<EmailBounceSummary | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  // Its own retry, so a failed panel can be reloaded without refreshing the whole dashboard.
  const [retryKey, setRetryKey] = useState(0);

  useEffect(() => {
    let live = true;
    setLoading(true);
    setError(null);
    axios
      .get<EmailBounceSummary>(`/api/email-bounces/summary?days=${days}`)
      .then((r) => { if (live) setData(r.data); })
      .catch(() => { if (live) setError('Could not load bounces and complaints'); })
      .finally(() => { if (live) setLoading(false); });
    return () => { live = false; };
  }, [days, refreshKey, retryKey]);

  const byDay = data?.byDay ?? [];

  return (
    <Panel className="ea-bounce-panel">
        <Typography variant="subtitle1" className="ea-chart-title">
          Bounces and complaints: last {days} days
        </Typography>

        {error && <ErrorMessage message={error} onRetry={() => setRetryKey((k) => k + 1)} />}
        {loading && !data && <SkeletonLoader rows={4} columns={4} />}

        {data && (
          <Box className="ea-bounce-body">
            <Box className="cc-stat-grid">
              <StatTile
                label="Bounces"
                value={n(data.total)}
                icon={<UnsubscribeOutlinedIcon />}
                sublabel={`${n(data.transient)} transient`}
                tone={data.total > 0 ? 'warning' : 'neutral'}
              />
              <StatTile
                label="Permanent"
                value={n(data.permanent)}
                icon={<HighlightOffOutlinedIcon />}
                sublabel="Blocked at once"
                tone={data.permanent > 0 ? 'danger' : 'neutral'}
              />
              <StatTile
                label="Complaints"
                value={n(data.complaints)}
                icon={<ReportGmailerrorredOutlinedIcon />}
                sublabel="Marked as spam"
                tone={data.complaints > 0 ? 'danger' : 'neutral'}
              />
              <StatTile
                label="Addresses blocked"
                value={n(data.suppressedTotal)}
                icon={<BlockOutlinedIcon />}
                sublabel="All time"
                to="/email-campaigns/blocked-addresses"
                linkLabel="Open blocked addresses"
              />
            </Box>

            {byDay.length > 0 ? (
              <LineChart
                xAxis={[{ data: byDay.map((d) => formatDayMonth(d.date)), scaleType: 'band', tickLabelStyle: { fontSize: 11 } }]}
                series={linearSeries([
                  { data: byDay.map((d) => d.bounces), label: 'Bounces', color: 'var(--red)' },
                  { data: byDay.map((d) => d.complaints), label: 'Complaints', color: 'var(--amber)' },
                ])}
                yAxis={COUNT_Y_AXIS}
                height={240}
                margin={{ top: 24, right: 24, bottom: 48, left: 44 }}
              />
            ) : (
              <EmptyState compact title="No bounces or complaints in this period" />
            )}

            <Box className="ea-charts-row">
              <Box className="ea-bounce-table">
                <Typography variant="subtitle2" className="ea-bounce-table-title">By sender address</Typography>
                <MetricTable<SenderRow>
                  rows={data.bySender ?? []}
                  getRowKey={(r) => r.from}
                  emptyLabel="Nothing to report"
                  columns={SENDER_COLUMNS}
                  defaultSort={{ key: 'count', dir: 'desc' }}
                />
              </Box>
              <Box className="ea-bounce-table">
                <Typography variant="subtitle2" className="ea-bounce-table-title">Top reasons</Typography>
                <MetricTable<ReasonRow>
                  rows={data.topReasons ?? []}
                  getRowKey={(r) => r.reason}
                  emptyLabel="Nothing to report"
                  columns={REASON_COLUMNS}
                  defaultSort={{ key: 'count', dir: 'desc' }}
                />
              </Box>
            </Box>
          </Box>
        )}
    </Panel>
  );
};

export default BouncePanel;
